import { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { AHButton, AHRadioButton, AHText, Box, TouchableBox } from "arkh";
import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { graphql, useMutation } from "react-relay";
import { StartFocusModeButtonText } from "./StartFocusModeButtonText";
import { SelectTemporaryScreenMutation } from "./__generated__/SelectTemporaryScreenMutation.graphql";

/*
    onCompleted: (data) => {
      const activeScreens = data.storecast.screens.filter(
        (screen) => screen.activeSession
      );
      if (activeScreens.length === 1) {
        setLocalScreen(activeScreens[0].id);
      } else {
        setLocalScreen(data.dashboard?.temporaryScreen?.id ?? null);
      }
    },
    */

export function SelectTemporaryScreen(props: {
  onStartFocusMode: () => void;
  onlineNonSelectedScreens: { id: string; name: string }[];
  onAddScreen: () => void;
  storecastId: string;
}) {
  const intl = useIntl();
  const [localTempScreenId, setLocalTempScreenId] = useState<string | null>(
    null
  );
  if (
    localTempScreenId === null &&
    props.onlineNonSelectedScreens.length === 1
  ) {
    setLocalTempScreenId(props.onlineNonSelectedScreens[0].id);
  }

  const [setTemporaryScreenId, setTemporaryScreenIdLoading] =
    useMutation<SelectTemporaryScreenMutation>(graphql`
      mutation SelectTemporaryScreenMutation($id: ID!, $screenId: ID!) {
        dashboard(dashboardId: $id) {
          setTemporaryScreenId(screenId: $screenId) {
            id
            screens {
              id
            }
            temporaryScreen {
              id
            }
          }
        }
      }
    `);

  const onStartWithSelectedScreen = useCallback(() => {
    if (localTempScreenId) {
      setTemporaryScreenId({
        variables: {
          id: props.storecastId,
          screenId: localTempScreenId,
        },
        onCompleted: props.onStartFocusMode,
      });
    }
  }, [
    localTempScreenId,
    props.onStartFocusMode,
    props.storecastId,
    setTemporaryScreenId,
  ]);

  return (
    <>
      <Box
        style={{
          justifyContent: "flex-start",
        }}
        spaceHorizontal="large"
      >
        <AHText
          spaceTop="medium"
          type="list-item-title"
          color="text-secondary"
          spaceBottom="medium"
        >
          <FormattedMessage
            defaultMessage="Choose a screen to show."
            description="Focus BS Select Temporary Screen title"
          />
        </AHText>

        <AHText type="detail" color="text-primary" spaceBottom="medium">
          <FormattedMessage
            defaultMessage="Which screen do you want to connect to?"
            description="Focus BS Select Temporary Screen subtitle"
          />
        </AHText>

        <BottomSheetScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
        >
          {props.onlineNonSelectedScreens.map((screen) => {
            const selected = localTempScreenId === screen.id;
            return (
              <Box key={screen.id} spaceRight="small">
                <SelectTemporaryScreenItem
                  screen={screen}
                  onPress={() => {
                    setLocalTempScreenId(screen.id);
                  }}
                  selected={selected}
                />
              </Box>
            );
          })}
        </BottomSheetScrollView>
      </Box>
      <Box
        spaceHorizontal="large"
        spaceBottom="medium"
        style={{ flex: 1, justifyContent: "flex-end" }}
      >
        <AHButton
          themeType="primary"
          title={<StartFocusModeButtonText />}
          onPress={onStartWithSelectedScreen}
          disabled={localTempScreenId === null}
          loading={setTemporaryScreenIdLoading}
        />
        <AHButton
          themeType="plain"
          title={
            <AHText type="subtitle" color="text-cta">
              {intl.formatMessage({
                defaultMessage: "Install new Screen",
                description: "SelectTemporaryScreenItem Install new Screen",
              })}
            </AHText>
          }
          onPress={props.onAddScreen}
        />
      </Box>
    </>
  );
}

function SelectTemporaryScreenItem(props: {
  selected: boolean;
  onPress: () => void;
  screen: { name: string };
}) {
  return (
    <TouchableBox
      color="surface-backgroundControl"
      cornerRadius="medium"
      style={{ flexDirection: "row", alignItems: "center", padding: 10 }}
      onPress={props.onPress}
    >
      <AHText type="title-small">{props.screen.name}</AHText>
      <Box spaceLeft="medium">
        <AHRadioButton selected={props.selected} />
      </Box>
    </TouchableBox>
  );
}
